import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stage, ChapterTag } from "@/components/Reveal";
import { Arch, Traveler, Thread } from "@/components/Motifs";

const ARRIVING_RANGES = {
  room: [0.08, 0.16, 0.3, 0.38],
  person: [0.42, 0.5, 0.6, 0.68],
  statement: [0.84, 0.92],
};

const useArrivalMotion = (progress) => ({
  bOp: useTransform(progress, [0.3, 0.5], [0, 0.5]),
  planeX: useTransform(progress, [0, 1], [60, -40]),
  planeRot: useTransform(progress, [0.7, 0.92], [6, 1.5]),
  plane2X: useTransform(progress, [0, 1], [-50, 30]),
  plane2Rot: useTransform(progress, [0.7, 0.92], [-5, -1]),
  pulseX: useTransform(progress, [0.56, 0.72], ["0vw", "47vw"]),
  pulseY: useTransform(progress, [0.56, 0.72], ["0vh", "-3vh"]),
  pulseS: useTransform(progress, [0.56, 0.72], [1, 0.62]),
  pulseOp: useTransform(progress, [0.55, 0.59, 0.68, 0.73], [0, 1, 1, 0]),
  answerOp: useTransform(progress, [0.73, 0.78, 0.87], [0, 1, 0]),
  answerScale: useTransform(progress, [0.73, 0.87], [0.5, 2.1]),
  farDoor: useTransform(progress, [0.74, 0.86], [0, 0.9]),
  wash: useTransform(progress, [0.72, 0.9], [0, 1]),
  entry: useTransform(progress, [0.03, 0.2], [0, 1]),
  thread: useTransform(progress, [0.66, 0.9], [0, 1]),
});

const ArrivalAtmosphere = ({ animation }) => (
  <>
    <motion.div className="th-wash" style={{ opacity: animation.wash }} />
    <motion.div className="th-glass" style={{ x: animation.planeX, rotate: animation.planeRot, right: "4%", top: "10%", width: "44vw", height: "72vh", background: "rgba(170,185,163,.15)", backdropFilter: "none", WebkitBackdropFilter: "none" }} />
    <motion.div className="th-glass" style={{ x: animation.plane2X, rotate: animation.plane2Rot, left: "2%", top: "16%", width: "30vw", height: "60vh", background: "rgba(183,200,206,.14)", backdropFilter: "none", WebkitBackdropFilter: "none" }} />
    <svg viewBox="0 0 1440 220" preserveAspectRatio="none" className="absolute bottom-0 left-0 w-full h-[24vh]" aria-hidden="true">
      <path d="M0 160 C 240 90, 420 190, 720 130 S 1200 70, 1440 150 V220 H0 Z" fill="var(--sage)" opacity=".16" />
      <path d="M0 190 C 260 140, 520 210, 820 165 S 1240 120, 1440 185 V220 H0 Z" fill="var(--blue)" opacity=".15" />
    </svg>
    <div className="absolute left-[38%] bottom-[10%] opacity-[.16]" aria-hidden="true"><Arch style={{ height: "36vh" }} stroke="var(--ink-soft)" sw={1} /></div>
    <div className="absolute right-[42%] bottom-[16%] opacity-[.1]" aria-hidden="true"><Arch style={{ height: "48vh" }} stroke="var(--sage-deep)" sw={1} /></div>
    <div className="th-ghostword" style={{ top: "12%", right: "6%", fontSize: "12vw" }} aria-hidden="true">elsewhere</div>
  </>
);

const ArrivalActors = ({ animation }) => (
  <>
    <div className="absolute left-[13%] bottom-[16%] w-[150px] md:w-[210px]">
      <div className="th-doorlight-soft" />
      <Arch className="w-full" stroke="var(--ink-soft)" sw={1.3} data-testid="arrival-arch" />
      <Traveler className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-12 md:h-14" data-testid="traveler-a" />
    </div>
    <div className="absolute right-[27%] bottom-[30%] w-[86px]" data-testid="distant-doorway">
      <motion.div className="th-doorlight-soft" style={{ opacity: animation.farDoor }} />
      <Arch className="w-full opacity-50" stroke="var(--muted)" sw={1} />
    </div>
    <motion.div className="absolute right-[14%] bottom-[22%]" style={{ opacity: animation.bOp }} data-testid="traveler-b">
      <Traveler className="h-10 md:h-12" tone="var(--muted)" />
    </motion.div>
    <motion.span className="th-dot" style={{ left: "19%", bottom: "31%", x: animation.pulseX, y: animation.pulseY, scale: animation.pulseS, opacity: animation.pulseOp, width: 9, height: 9 }} data-testid="arrival-pulse" />
    <motion.span className="absolute rounded-full border border-[var(--amber)]" style={{ right: "16%", bottom: "31.5%", opacity: animation.answerOp, scale: animation.answerScale, width: 26, height: 26 }} data-testid="arrival-answer-pulse" />
  </>
);

const ArrivalThreads = ({ animation }) => (
  <>
    <Thread d="M -30 500 C 120 505, 150 660, 205 725" viewBox="0 0 1000 1000" className="absolute inset-0 w-full h-full" progress={animation.entry} stroke="var(--sage-deep)" sw={1.3} />
    <Thread d="M 210 730 C 420 600, 640 590, 800 700" viewBox="0 0 1000 1000" className="absolute inset-0 w-full h-full" progress={animation.thread} stroke="var(--ink-soft)" sw={1.2} />
  </>
);

export default function Arriving() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const animation = useArrivalMotion(p);

  return (
    <section id="scene-arriving" ref={ref} className="th-scene" style={{ height: "230vh" }} data-testid="scene-arriving">
      <div className="th-sticky">
        <ChapterTag index="04" name="Someone Else Is Arriving" />
        <ArrivalAtmosphere animation={animation} />
        <ArrivalActors animation={animation} />
        <ArrivalThreads animation={animation} />
        <Stage progress={p} range={ARRIVING_RANGES.room}>
          <h3 className="th-question th-halo">A shared room is opening.</h3>
        </Stage>
        <Stage progress={p} range={ARRIVING_RANGES.person}>
          <h3 className="th-question th-halo">
            Somewhere else, another person
            <br />
            is choosing how to arrive.
          </h3>
        </Stage>
        <Stage progress={p} range={ARRIVING_RANGES.statement} hold>
          <h3 className="th-statement th-halo" data-testid="arrivals-statement">
            One room. Two arrivals.
          </h3>
        </Stage>
      </div>
    </section>
  );
}
